import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { MoonIcon, SparkleIcon, StarIcon } from './Icons';
export function Hero() {
  return <section className="relative pt-20 pb-24 overflow-hidden px-6 lg:px-8" data-id="element-86">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 text-gold/30 animate-pulse" data-id="element-87">
        <SparkleIcon className="w-12 h-12" data-id="element-88" />
      </div>
      <div className="absolute top-40 right-10 text-gold/20" data-id="element-89">
        <MoonIcon className="w-24 h-24 transform rotate-12" data-id="element-90" />
      </div>
      <div className="absolute bottom-20 left-20 text-gold/40" data-id="element-91">
        <StarIcon className="w-8 h-8" data-id="element-92" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-93">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12" data-id="element-94">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center lg:text-left" data-id="element-95">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blush/30 text-indigo text-sm font-medium mb-8" data-id="element-96">
              <MoonIcon className="w-4 h-4 text-indigo" data-id="element-97" />
              <span data-id="element-98">Guía de relatos infantiles para dormir en paz</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-indigo leading-tight mb-6" data-id="element-99">
              Tu hijo se dormirá en calma en{' '}
              <span className="text-gold italic" data-id="element-100">menos de 15 minutos.</span>
            </h1>

            <p className="text-lg md:text-xl text-indigo/80 max-w-2xl mx-auto lg:mx-0 mb-10 leading-relaxed" data-id="element-101">
              33 cuentos "<span className="glitter-violet" data-id="element-102">espirituales</span>"
              que ayudan a calmar la mente de los niños antes de dormir mientras
              aprenden <span className="glitter-violet" data-id="element-103">valores</span> como el{' '}
              <span className="glitter-violet" data-id="element-104">amor</span>, la{' '}
              <span className="glitter-violet" data-id="element-105">fe</span>, la{' '}
              <span className="glitter-violet" data-id="element-106">bondad</span> y la{' '}
              <span className="glitter-violet" data-id="element-107">protección</span> espiritual.
            </p>

            <p className="text-base md:text-lg text-indigo/70 max-w-3xl mx-auto lg:mx-0 mb-4" data-id="element-108">
              Con historias breves, emocionantes y fáciles de comprender, esta
              guía transforma la rutina nocturna en un instante de conexión,
              tranquilidad y luz para toda la familia.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="relative" data-id="element-109">
            <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-blush/10 to-indigo/10 rounded-3xl blur-2xl" data-id="element-110"></div>
            <img src="https://cdn.magicpatterns.com/uploads/fcUBNp67MDXVcAKoedr6Rg/FOTO_PRINCIPAL_CUENTOS_INFANTILES.png" alt="Historias infantiles para dormir en paz - 33 cuentos" width={800} height={800} loading="eager" decoding="sync" fetchPriority="high" className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl shadow-indigo/30" data-id="element-111" />
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12 text-left" data-id="element-112">
          <div className="flex items-center gap-3 bg-white/50 p-4 rounded-2xl" data-id="element-113">
            <span className="text-2xl" data-id="element-114">📖</span>
            <span className="font-medium text-indigo" data-id="element-115">
              Historias rápidas y atrapantes
            </span>
          </div>
          <div className="flex items-center gap-3 bg-white/50 p-4 rounded-2xl" data-id="element-116">
            <span className="text-2xl" data-id="element-117">🌙</span>
            <span className="font-medium text-indigo" data-id="element-118">
              Ideales para crear una rutina nocturna más tranquila
            </span>
          </div>
          <div className="flex items-center gap-3 bg-white/50 p-4 rounded-2xl" data-id="element-119">
            <span className="text-2xl" data-id="element-120">👨‍👩‍👧</span>
            <span className="font-medium text-indigo" data-id="element-121">
              Generan momentos especiales en familia
            </span>
          </div>
          <div className="flex items-center gap-3 bg-white/50 p-4 rounded-2xl" data-id="element-122">
            <span className="text-2xl" data-id="element-123">✨</span>
            <span className="font-medium text-indigo" data-id="element-124">
              Explican valores espirituales con ternura y sensibilidad
            </span>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="flex flex-col items-center" data-id="element-125">
          <a href="#oferta" className="inline-flex items-center justify-center gap-2 bg-gold text-indigo font-bold text-lg md:text-xl px-10 py-5 rounded-full hover:scale-105 transition-transform duration-300 shadow-lg shadow-gold/20" data-id="element-126">
            ✨ QUIERO DORMIR EN PAZ
          </a>

          <div className="flex items-center gap-2 mt-6" data-id="element-127">
            <div className="flex text-gold" data-id="element-128">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-129" />)}
            </div>
            <span className="text-indigo/80 font-medium" data-id="element-130">
              4.8/5 Basado en +300 Opiniones
            </span>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mt-12 w-full max-w-md mx-auto" data-id="element-131">
            <p className="text-center text-xs font-bold tracking-widest uppercase text-indigo/60 mb-4" data-id="element-132">
              💬 Opiniones reales de nuestras lectoras
            </p>
            <img src="https://cdn.magicpatterns.com/uploads/fin1GGx9SijDiuvcoeLMK5/TESTIMONIO_33_CUENTOS_INFANTILES.png" alt="Testimonios reales de madres que ya están usando los 33 cuentos infantiles" width={800} height={600} loading="lazy" decoding="async" className="w-full rounded-2xl shadow-xl shadow-indigo/20" data-id="element-133" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}